// components/SnSortableFormControls.tsx
/**
 * 배치 편집에서 칸을 끌어 옮기고 크기를 바꾸는 부품 (2026-10-06).
 *
 * SnDndFormControls(react-dnd) 를 대신한다. 그 부품은 마우스가 다른 칸을 지날 때마다 배열을 바꿔
 * 끄는 동안 화면 전체가 다시 그려졌고(받는 쪽이 그때마다 초안을 고치면 칸이 통째로 새로 생겨 끌던 칸을 놓쳤다),
 * 크기는 85px 고정으로 계산해 실제 칸 폭과 맞지 않았다.
 *
 * 여기서는 상단 탭(TabBar)처럼 dnd-kit 을 쓴다.
 * - 끄는 동안 이웃 칸은 CSS transform 으로만 비켜 나고, 실제 순서 변경(onMove)은 손을 놓을 때 한 번만 부른다.
 * - 잡은 칸은 그림자 진 복제본(DragOverlay)이 마우스를 따라오고, 원래 자리는 파란 점선 빈칸으로 남는다.
 * - 크기 조정은 그리드의 실제 칸 폭 · 줄 높이로 계산해 1칸 ~ 한 줄 전체로 맞추고, 손을 놓을 때 한 번만 부른다.
 *
 * 조회조건(SearchPanel) · 입력 폼(SnForm · FreeForm · SharedForm)이 쓴다. 예전 디자이너(Designer · Designer2)는 그대로 SnDndFormControls.
 * 크기 조정 손잡이는 resizeControl 을 넘긴 화면(조회조건)에만 보인다.
 */

import React, { createContext, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SnLabel from "./SnLabel";
import SnFindFormContols from "./SnFindFormContols";
import { TpCtrl } from "@shared/type/CommonType";

/** 크기 조정 줄 수 상한 — 조회조건 · 입력 폼에서 이보다 높은 칸은 없다 */
const MAX_ROWSPAN = 6;

/** 복제본에서 지울 표시(삭제 X · 크기 조정 손잡이) */
const HANDLE_ATTR = "data-sn-handle";

/* 칸이 자기 DOM 을 등록한다 — 끌기 시작할 때 그 모습을 그대로 복제해 마우스를 따라오게 한다.
   React 로 다시 그리면 콤보 등이 새로 마운트되며 조회를 다시 할 수 있어 DOM 복제를 쓴다 */
const NodeRegistry = createContext<(id: string, el: HTMLElement | null) => void>(() => {});

/** 칸을 가리키는 값 — ids · sortId · key 에 같이 쓴다. 같은 no_line 이 겹쳐도 갈리게 파라미터를 붙인다 */
export const sortIdOf = (control: any) => `${control.no_line}-${control.cd_param}`;

interface AreaProps {
  /** 칸 식별자 목록 — children 의 sortId 와 같은 순서 */
  ids: string[];
  /** 손을 놓았을 때 한 번 부른다 */
  onMove: (fromIdx: number, toIdx: number) => void;
  children: React.ReactNode;
}

/** 칸들을 감싼다. DOM 을 그리지 않으므로 그리드 안에 그대로 두면 칸들이 그리드 자식으로 남는다 */
export const SnSortableFormArea: React.FC<AreaProps> = ({ ids, onMove, children }) => {
  const nodesRef = useRef(new Map<string, HTMLElement>());
  const [overlayNode, setOverlayNode] = useState<HTMLElement | null>(null);

  // 5px 이상 움직여야 끌기로 본다. 이게 없으면 삭제(X) 클릭이 끌기로 먹힌다
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const register = (id: string, el: HTMLElement | null) => {
    if (el) nodesRef.current.set(id, el);
    else nodesRef.current.delete(id);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const el = nodesRef.current.get(String(event.active.id));
    if (!el) return;
    const clone = el.cloneNode(true) as HTMLElement;
    clone.querySelectorAll(`[${HANDLE_ATTR}]`).forEach((n) => n.remove());
    // 칸 자리 · 테두리 · 이동 효과는 복제본 바깥 틀이 맡는다
    Object.assign(clone.style, {
      transform: "",
      transition: "",
      border: "none",
      background: "transparent",
      width: "100%",
      height: "100%",
    });
    setOverlayNode(clone);
  };

  // 끄는 동안 배열은 그대로 두고, 놓을 때 한 번만 옮긴다 (TabBar 와 같은 방식)
  const handleDragEnd = (event: DragEndEvent) => {
    setOverlayNode(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onMove(from, to);
  };

  return (
    <NodeRegistry.Provider value={register}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setOverlayNode(null)}
      >
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          {children}
        </SortableContext>
        {/* 그리드 안에 두면 조상의 overflow · 쌓임 순서에 걸린다 — body 로 띄운다 */}
        {createPortal(
          <DragOverlay
            zIndex={1300}
            dropAnimation={{
              duration: 200,
              easing: "cubic-bezier(0.2, 0, 0, 1)",
              sideEffects: defaultDropAnimationSideEffects({
                styles: { active: { opacity: "0" } },
              }),
            }}
          >
            {overlayNode ? (
              <div
                ref={(el) => {
                  if (el && el.firstChild !== overlayNode) el.replaceChildren(overlayNode);
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  background: "white",
                  border: "1px solid #1976d2",
                  borderRadius: 4,
                  boxShadow: "0 10px 28px rgba(0, 0, 0, 0.18)",
                  transform: "scale(1.02)",
                  cursor: "grabbing",
                  pointerEvents: "none",
                }}
              />
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </NodeRegistry.Provider>
  );
};

interface Control {
  tp_ctrl: TpCtrl;
  no_line: string;
  qt_rowspan: number;
  qt_colspan: number;
  nm_label?: string;
  cd_param: string;
}

interface ItemProps {
  /** SnSortableFormArea 의 ids 에 넣은 값 */
  sortId: string;
  isSearchPanel?: boolean;
  isFormPanel?: boolean;
  labletype: string;
  control: Control;
  id: number;
  index: number;
  formData: any;
  requireds: Record<string, boolean>;
  popupCons: Record<string, any>;
  popupRef?: any;
  setRef: (line: string, node: HTMLDivElement | null) => void;
  setInputRef: (el: any, index: number, isReadOnly: boolean) => void;
  handleMoveFocus: (index: number, target: string) => void;
  onTextValueChanged: (param: string, data: string) => void;
  onNumberValueChanged: (param: string, data: number) => void;
  onPeriodValueChanged: (param: string, start: string, end: string) => void;
  onCodeValueChanged: (
    code: string,
    data: string,
    name: string,
    data2: string,
    rowData?: any
  ) => void;
  setReadOnly: (param: string) => boolean;
  onButtonClick: (param: string) => void;
  removeControl?: (idx: number, id: number) => void;
  /** 손을 놓았을 때 한 번 부른다. 값은 이미 1칸 ~ 한 줄 전체, 1 ~ MAX_ROWSPAN 줄로 맞춘 것 */
  resizeControl?: (id: number, colspan: number, rowspan: number) => void;
  filter: Record<string, any>;
  labelSize?: number;
  /** 삭제(X) 버튼을 칸 우상단 대신 라벨 바로 옆에 붙인다 (메뉴디자이너) */
  removeAtLabel?: boolean;
}

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/** 칸 하나. 칸 전체가 끌기 손잡이이고, 안쪽 입력은 누를 수 없게 막는다 */
export const SnSortableFormControl: React.FC<ItemProps> = React.memo(
  ({
    sortId,
    isSearchPanel = false,
    isFormPanel = false,
    labletype,
    control,
    id,
    index,
    formData,
    requireds,
    popupCons,
    popupRef,
    setRef,
    setInputRef,
    handleMoveFocus,
    onTextValueChanged,
    onNumberValueChanged,
    onPeriodValueChanged,
    onCodeValueChanged,
    setReadOnly,
    onButtonClick,
    removeControl,
    resizeControl,
    filter,
    labelSize,
    removeAtLabel = false,
  }) => {
    const register = useContext(NodeRegistry);
    const nodeRef = useRef<HTMLDivElement | null>(null);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
      useSortable({ id: sortId });

    /* 크기 조정 중 미리보기 — 이 칸만 다시 그려 그리드가 바로 다시 배치된다. 놓을 때 resizeControl 로 확정 */
    const [preview, setPreview] = useState<{ col: number; row: number } | null>(null);
    const resizeStart = useRef<{
      x: number;
      y: number;
      width: number;
      height: number;
      colStep: number;
      rowStep: number;
      colGap: number;
      rowGap: number;
      colCount: number;
    } | null>(null);

    const colspan = preview?.col ?? control.qt_colspan;
    const rowspan = preview?.row ?? control.qt_rowspan;
    const isRequired = Object.hasOwn(requireds, control.cd_param);
    // 라벨이 실제로 그려지는가 — removeAtLabel 일 때 X 위치를 정한다
    const hasLabel = Boolean(
      control.nm_label &&
        control.tp_ctrl !== "SnGroupLabel" &&
        control.tp_ctrl !== "SnLabel" &&
        control.tp_ctrl !== "SnButton"
    );

    /* 그리드의 실제 칸 폭 · 줄 높이를 잰다. 칸은 모두 1fr 이라 첫 칸 폭이 곧 한 칸 폭이다 */
    const onResizeDown = (e: React.PointerEvent<HTMLElement>) => {
      e.stopPropagation(); // 칸 끌기가 시작되지 않게
      e.preventDefault();
      const node = nodeRef.current;
      const grid = node?.parentElement;
      if (!node || !grid) return;
      const cs = getComputedStyle(grid);
      const tracks = cs.gridTemplateColumns.split(" ").map(parseFloat).filter((n) => !isNaN(n));
      if (tracks.length === 0) return;
      const colGap = parseFloat(cs.columnGap) || 0;
      const rowGap = parseFloat(cs.rowGap) || 0;
      const rect = node.getBoundingClientRect();
      const rows = Math.max(1, Number(control.qt_rowspan) || 1);
      resizeStart.current = {
        x: e.clientX,
        y: e.clientY,
        width: rect.width,
        height: rect.height,
        colStep: tracks[0] + colGap,
        rowStep: (rect.height + rowGap) / rows,
        colGap,
        rowGap,
        colCount: tracks.length,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    };

    const onResizeMove = (e: React.PointerEvent<HTMLElement>) => {
      const s = resizeStart.current;
      if (!s) return;
      const col = clamp(Math.round((s.width + e.clientX - s.x + s.colGap) / s.colStep), 1, s.colCount);
      const row = clamp(Math.round((s.height + e.clientY - s.y + s.rowGap) / s.rowStep), 1, MAX_ROWSPAN);
      if (col !== preview?.col || row !== preview?.row) setPreview({ col, row });
    };

    const onResizeUp = (e: React.PointerEvent<HTMLElement>) => {
      if (!resizeStart.current) return;
      resizeStart.current = null;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      if (preview && (preview.col !== control.qt_colspan || preview.row !== control.qt_rowspan)) {
        resizeControl?.(id, preview.col, preview.row);
      }
      setPreview(null);
    };

    const isResizing = preview !== null;

    return (
      <div
        ref={(node) => {
          nodeRef.current = node;
          setNodeRef(node);
          register(sortId, node);
          setRef(control.no_line, node);
        }}
        {...attributes}
        {...listeners}
        // 테두리 · 손잡이 표시는 마우스 올림(hover)으로만 바뀐다 — 상태로 두면 끄는 동안 지나가는 칸마다 다시 그린다
        className={
          "group flex justify-start rounded " +
          (isDragging
            ? "border border-dashed border-[#1976d2] bg-[rgba(25,118,210,0.06)]"
            : isResizing
              ? "border border-solid border-[#1976d2] bg-[rgba(25,118,210,0.06)]"
              : "border border-dashed border-[#c3c8d4] hover:border-solid hover:border-[#1976d2] hover:bg-[rgba(25,118,210,0.04)]")
        }
        style={{
          position: "relative",
          gridColumn: `auto/span ${colspan}`,
          gridRow: `auto/span ${rowspan}`,
          transform: CSS.Translate.toString(transform),
          transition: isResizing ? undefined : transition,
          cursor: isDragging ? "grabbing" : "grab",
          touchAction: "none",
          outline: "none",
        }}
      >
        {/* 끄는 동안 원래 자리는 빈칸으로 — 모양은 복제본이 따라간다 */}
        <div
          className="flex justify-start w-full"
          style={{ visibility: isDragging ? "hidden" : "visible" }}
        >
          {hasLabel ? (
            <div style={{ display: "flex", alignItems: "center", flexShrink: 0, paddingLeft: 6 }}>
              <SnLabel
                text={control.nm_label ?? ""}
                type={labletype}
                color={isRequired ? "#0b0c69" : "black"}
                size={labelSize}
              />
              {removeAtLabel && !isRequired && (
                <span
                  {...{ [HANDLE_ATTR]: "" }}
                  className="hidden group-hover:inline-flex items-center justify-center"
                  style={{ marginLeft: 2, marginRight: 2, cursor: "pointer", zIndex: 10 }}
                  title="삭제"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => removeControl?.(index, id)}
                >
                  <HighlightOffIcon sx={{ fontSize: 14 }} color="error" />
                </span>
              )}
            </div>
          ) : null}

          {/* 안쪽 입력은 누를 수 없다 — 칸 전체가 끌기 손잡이 */}
          <div className="w-full" style={{ pointerEvents: "none" }}>
            <SnFindFormContols
              isSearchPanel={isSearchPanel}
              isFormPanel={isFormPanel}
              control={control as any}
              index={index}
              formData={formData}
              requireds={requireds}
              popupCons={popupCons}
              setRef={setRef}
              setInputRef={setInputRef}
              handleMoveFocus={handleMoveFocus}
              onTextValueChanged={onTextValueChanged}
              onNumberValueChanged={onNumberValueChanged}
              onPeriodValueChanged={onPeriodValueChanged}
              onCodeValueChanged={onCodeValueChanged}
              setReadOnly={setReadOnly}
              onButtonClick={(param: string) => onButtonClick?.(param)}
              filter={filter}
              popupRef={popupRef}
            />
          </div>
          {control.nm_label && control.tp_ctrl.includes("AfterLabel") ? (
            <SnLabel
              text={control.nm_label}
              type={"AfterLabel"}
              color={isRequired ? "#0b0c69" : "black"}
            />
          ) : null}
        </div>

        {!isRequired && !isDragging && (
          <>
            {/* 삭제 — removeAtLabel 이고 라벨이 있으면 라벨 옆에 이미 그렸다 */}
            {!(removeAtLabel && hasLabel) && (
              <span
                {...{ [HANDLE_ATTR]: "" }}
                className="hidden group-hover:flex items-center justify-center"
                style={{
                  position: "absolute",
                  top: 2,
                  right: 10,
                  cursor: "pointer",
                  zIndex: 10,
                  background: "white",
                  borderRadius: "50%",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  padding: 2,
                }}
                title="삭제"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => removeControl?.(index, id)}
              >
                <HighlightOffIcon fontSize="small" color="error" />
              </span>
            )}

            {/* 크기 조정 손잡이 — 끄는 중에는 손을 놓을 때까지 보인다. 크기 조정을 넘긴 화면에만 */}
            {resizeControl && <span
              {...{ [HANDLE_ATTR]: "" }}
              className={(isResizing ? "flex" : "hidden group-hover:flex") + " items-end justify-end"}
              style={{
                position: "absolute",
                right: 2,
                bottom: 2,
                width: 14,
                height: 14,
                cursor: "nwse-resize",
                zIndex: 11,
              }}
              title="크기 조정"
              onPointerDown={onResizeDown}
              onPointerMove={onResizeMove}
              onPointerUp={onResizeUp}
              onPointerCancel={onResizeUp}
            >
              <svg width="14" height="14" viewBox="0 0 18 18">
                <polygon points="18,18 0,18 18,0" fill="#1976d2" opacity="0.35" />
                <polyline points="12,18 18,12" stroke="#1976d2" strokeWidth="2" fill="none" />
              </svg>
            </span>}

            {/* 크기 조정 중 지금 크기 */}
            {isResizing && (
              <span
                {...{ [HANDLE_ATTR]: "" }}
                style={{
                  position: "absolute",
                  right: 20,
                  bottom: 2,
                  zIndex: 11,
                  padding: "0 6px",
                  borderRadius: 3,
                  background: "#1976d2",
                  color: "white",
                  fontSize: 11,
                  lineHeight: "16px",
                  pointerEvents: "none",
                }}
              >
                {colspan} × {rowspan}
              </span>
            )}
          </>
        )}
      </div>
    );
  }
);

export default SnSortableFormControl;
